import { useCallback, useEffect, useMemo, useState } from 'react';
import { SectionCard, TabsRow } from '@/components/DataList';
import { ImagePicker } from '@/components/ImagePicker';
import { ListTable, ScreenFrame } from '@/components/Screen';
import { Icon } from '@/components/icons';
import { VISIBILITY_OPTIONS } from '@/components/panelOptions';
import { admin } from '@/api/endpoints';
import { errorMessage, pickList, type JsonMap } from '@/api/client';
import {
  Button,
  Checkbox,
  Empty,
  ErrorBox,
  FlashMessage,
  FormActions,
  Input,
  Loading,
  Money,
  Select,
  StatusBadge,
  Textarea,
  VisibilitySelect,
} from '@/components/ui';
import { formatDateTime, str } from '@/utils/format';

type Tab = 'categories' | 'products' | 'orders';

type BillParam = {
  name: string;
  label: string;
  type: 'text' | 'phone' | 'number';
  required: boolean;
  placeholder?: string;
};

type CategoryForm = {
  id: number;
  parent_id: number;
  name: string;
  image_url: string;
  is_active: number;
  sort_order: number;
};

type ProductForm = {
  id: number;
  category_id: number;
  name: string;
  description: string;
  image_url: string;
  price_syp: string;
  is_active: number;
  sort_order: number;
  params: BillParam[];
};

const EMPTY_CATEGORY: CategoryForm = {
  id: 0,
  parent_id: 0,
  name: '',
  image_url: '',
  is_active: 1,
  sort_order: 0,
};

const EMPTY_PRODUCT: ProductForm = {
  id: 0,
  category_id: 0,
  name: '',
  description: '',
  image_url: '',
  price_syp: '',
  is_active: 1,
  sort_order: 0,
  params: [],
};

function asParams(raw: unknown): BillParam[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((item, i) => {
    if (typeof item === 'string') {
      return { name: `field_${i + 1}`, label: item, type: 'text', required: true };
    }
    const row = (item && typeof item === 'object' ? item : {}) as JsonMap;
    const typeRaw = str(row.type, 'text');
    const type: BillParam['type'] =
      typeRaw === 'phone' || typeRaw === 'number' ? typeRaw : 'text';
    const name = str(row.name, `field_${i + 1}`).replace(/[^a-zA-Z0-9_]/g, '') || `field_${i + 1}`;
    return {
      name,
      label: str(row.label, name),
      type,
      required: Boolean(row.required),
      placeholder: str(row.placeholder, ''),
    };
  });
}

function orderParams(order: JsonMap): Array<[string, string]> {
  const raw = order.params;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return [];
  return Object.entries(raw as Record<string, unknown>).map(([k, v]) => [k, str(v)]);
}

export function BillsAdminScreen() {
  const [tab, setTab] = useState<Tab>('categories');
  const [categories, setCategories] = useState<JsonMap[]>([]);
  const [products, setProducts] = useState<JsonMap[]>([]);
  const [orders, setOrders] = useState<JsonMap[]>([]);
  const [categoryForm, setCategoryForm] = useState<CategoryForm>(EMPTY_CATEGORY);
  const [productForm, setProductForm] = useState<ProductForm>(EMPTY_PRODUCT);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState('');
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [references, setReferences] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setError('');
    const [cats, prods, ords] = await Promise.all([
      admin.billsCategories(),
      admin.billsProducts(),
      admin.billsOrders(),
    ]);
    if (!cats.success && !prods.success && !ords.success) {
      setError(errorMessage(cats));
      setLoading(false);
      return;
    }
    if (cats.success) setCategories(pickList(cats, 'data', 'categories', 'items'));
    if (prods.success) setProducts(pickList(prods, 'data', 'products', 'items'));
    if (ords.success) setOrders(pickList(ords, 'data', 'orders', 'items'));
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (tab !== 'orders') return;
    const timer = window.setInterval(() => void load(), 5000);
    return () => window.clearInterval(timer);
  }, [tab, load]);

  const openOrders = useMemo(
    () => orders.filter((o) => ['pending', 'processing'].includes(str(o.status))).length,
    [orders],
  );

  async function saveCategory() {
    if (!categoryForm.name.trim()) {
      setFlash('اسم القسم مطلوب.');
      return;
    }
    setBusy(true);
    const res = await admin.saveBillsCategory({
      id: categoryForm.id || undefined,
      parent_id: categoryForm.parent_id || 0,
      name: categoryForm.name.trim(),
      image_url: categoryForm.image_url,
      is_active: categoryForm.is_active,
      sort_order: categoryForm.sort_order,
    });
    setBusy(false);
    setFlash(errorMessage(res, res.success ? 'تم حفظ قسم الفواتير.' : 'تعذر الحفظ.'));
    if (res.success) {
      setCategoryForm(EMPTY_CATEGORY);
      void load();
    }
  }

  async function saveProduct() {
    if (!productForm.name.trim() || !productForm.price_syp.trim()) {
      setFlash('اسم المنتج وسعره مطلوبان.');
      return;
    }
    setBusy(true);
    const res = await admin.saveBillsProduct({
      id: productForm.id || undefined,
      category_id: productForm.category_id || 0,
      name: productForm.name.trim(),
      description: productForm.description,
      image_url: productForm.image_url,
      price_syp: Number(productForm.price_syp),
      is_active: productForm.is_active,
      sort_order: productForm.sort_order,
      params: productForm.params,
    });
    setBusy(false);
    setFlash(errorMessage(res, res.success ? 'تم حفظ منتج الفاتورة.' : 'تعذر الحفظ.'));
    if (res.success) {
      setProductForm(EMPTY_PRODUCT);
      void load();
    }
  }

  async function removeEntity(path: 'category' | 'product', id: unknown, name: string) {
    if (!window.confirm(`حذف ${name}؟ الطلبات القديمة لن تُحذف.`)) return;
    const res =
      path === 'category'
        ? await admin.deleteBillsCategory({ id })
        : await admin.deleteBillsProduct({ id });
    setFlash(errorMessage(res, res.success ? 'تم الحذف.' : 'تعذر الحذف.'));
    if (res.success) void load();
  }

  async function resolveOrder(order: JsonMap, decision: 'success' | 'reject') {
    const ok = window.confirm(
      decision === 'success'
        ? 'تأكيد تنفيذ الطلب؟'
        : 'رفض الطلب وإعادة كامل المبلغ للمستخدم؟',
    );
    if (!ok) return;
    const id = str(order.id);
    const res = await admin.resolveBillsOrder({
      id: order.id,
      decision,
      note: notes[id] || '',
      reference: references[id] || '',
    });
    setFlash(
      errorMessage(
        res,
        res.success
          ? decision === 'success'
            ? 'تم اعتماد تنفيذ الطلب.'
            : 'تم رفض الطلب وإرجاع الرصيد.'
          : 'تعذر معالجة الطلب.',
      ),
    );
    if (res.success) void load();
  }

  function editProduct(row: JsonMap) {
    setProductForm({
      id: Number(row.id) || 0,
      category_id: Number(row.category_id) || 0,
      name: str(row.name, ''),
      description: str(row.description, ''),
      image_url: str(row.image_url, ''),
      price_syp: str(row.price_syp ?? row.price, ''),
      is_active: Number(row.is_active ?? 1) ? 1 : 0,
      sort_order: Number(row.sort_order) || 0,
      params: asParams(row.params),
    });
    setTab('products');
  }

  function updateParam(index: number, key: keyof BillParam, value: string | boolean) {
    setProductForm((prev) => ({
      ...prev,
      params: prev.params.map((field, i) => {
        if (i !== index) return field;
        if (key === 'name' && typeof value === 'string') {
          return { ...field, name: value.replace(/[^a-zA-Z0-9_]/g, '') };
        }
        return { ...field, [key]: value };
      }),
    }));
  }

  if (loading) return <Loading />;
  if (error) return <ErrorBox message={error} onRetry={() => void load()} />;

  return (
    <ScreenFrame
      mode="admin"
      icon="bill"
      className="admin-bills-page"
      title="إدارة الفواتير"
      subtitle="إدارة أقسام ومنتجات الفواتير والطلبات المرتبطة بمحفظة الوحدات."
      actions={
        <Button type="button" variant="secondary" onClick={() => void load()}>
          <Icon name="refresh" size={17} /> تحديث
        </Button>
      }
    >
      <FlashMessage message={flash} />

      <TabsRow
        tabs={[
          { key: 'categories', label: 'الأقسام' },
          { key: 'products', label: 'المنتجات' },
          { key: 'orders', label: `الطلبات (${openOrders})` },
        ]}
        value={tab}
        onChange={setTab}
      />

      {tab === 'categories' ? (
        <>
          <SectionCard
            title={categoryForm.id ? 'تعديل قسم' : 'إضافة قسم'}
            icon={<Icon name="orders" size={24} />}
          >
            <div className="form-grid two-columns">
              <Input
                label="اسم القسم"
                value={categoryForm.name}
                onChange={(e) => setCategoryForm((f) => ({ ...f, name: e.target.value }))}
              />
              <Select
                label="القسم الأب"
                value={String(categoryForm.parent_id)}
                onChange={(v) => setCategoryForm((f) => ({ ...f, parent_id: Number(v) }))}
                options={[
                  { value: '0', label: 'قسم رئيسي' },
                  ...categories
                    .filter((c) => Number(c.id) !== categoryForm.id)
                    .map((c) => ({ value: str(c.id), label: str(c.name) })),
                ]}
              />
              <ImagePicker
                value={categoryForm.image_url}
                onChange={(url) => setCategoryForm((f) => ({ ...f, image_url: url }))}
                onNotify={(message) => setFlash(message)}
              />
              <VisibilitySelect
                value={categoryForm.is_active}
                onChange={(v) => setCategoryForm((f) => ({ ...f, is_active: Number(v) }))}
                options={[
                  { value: '1', label: 'ظاهر' },
                  { value: '0', label: 'مخفي' },
                ]}
              />
              <Input
                label="الترتيب"
                type="number"
                value={String(categoryForm.sort_order)}
                onChange={(e) =>
                  setCategoryForm((f) => ({ ...f, sort_order: Number(e.target.value) }))
                }
              />
            </div>
            <FormActions>
              <Button type="button" disabled={busy} onClick={() => void saveCategory()}>
                <Icon name="edit" size={17} /> حفظ القسم
              </Button>
              {categoryForm.id > 0 ? (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setCategoryForm(EMPTY_CATEGORY)}
                >
                  إلغاء
                </Button>
              ) : null}
            </FormActions>
          </SectionCard>

          <SectionCard title="الأقسام الحالية" icon={<Icon name="chart" size={24} />}>
            {categories.length === 0 ? (
              <Empty title="لا توجد أقسام" />
            ) : (
              <ListTable headers={['القسم', 'الأب', 'المنتجات', 'الحالة', 'إجراءات']}>
                <tbody>
                  {categories.map((c) => (
                    <tr key={str(c.id)}>
                      <td>
                        <strong>{str(c.name)}</strong>
                      </td>
                      <td>{str(c.parent_name, 'رئيسي')}</td>
                      <td>{str(c.products_count ?? 0)}</td>
                      <td>{Number(c.is_active) ? 'ظاهر' : 'مخفي'}</td>
                      <td>
                        <div className="table-actions">
                          <button
                            type="button"
                            className="icon-btn"
                            title="تعديل"
                            onClick={() =>
                              setCategoryForm({
                                id: Number(c.id) || 0,
                                parent_id: Number(c.parent_id) || 0,
                                name: str(c.name, ''),
                                image_url: str(c.image_url, ''),
                                is_active: Number(c.is_active ?? 1) ? 1 : 0,
                                sort_order: Number(c.sort_order) || 0,
                              })
                            }
                          >
                            <Icon name="edit" size={16} />
                          </button>
                          <button
                            type="button"
                            className="icon-btn danger"
                            title="حذف"
                            onClick={() => void removeEntity('category', c.id, str(c.name))}
                          >
                            <Icon name="close" size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </ListTable>
            )}
          </SectionCard>
        </>
      ) : null}

      {tab === 'products' ? (
        <>
          <SectionCard
            title={productForm.id ? 'تعديل منتج فاتورة' : 'إضافة منتج فاتورة'}
            icon={<Icon name="package" size={24} />}
          >
            <div className="form-grid two-columns">
              <Input
                label="اسم المنتج"
                value={productForm.name}
                onChange={(e) => setProductForm((f) => ({ ...f, name: e.target.value }))}
              />
              <Select
                label="القسم"
                value={String(productForm.category_id)}
                onChange={(v) => setProductForm((f) => ({ ...f, category_id: Number(v) }))}
                options={[
                  { value: '0', label: 'بدون قسم' },
                  ...categories.map((c) => ({ value: str(c.id), label: str(c.name) })),
                ]}
              />
              <Input
                label="السعر من محفظة الوحدات"
                value={productForm.price_syp}
                inputMode="decimal"
                onChange={(e) => setProductForm((f) => ({ ...f, price_syp: e.target.value }))}
              />
              <VisibilitySelect
                value={productForm.is_active}
                onChange={(v) => setProductForm((f) => ({ ...f, is_active: Number(v) }))}
                options={VISIBILITY_OPTIONS}
              />
              <Input
                label="الترتيب"
                type="number"
                value={String(productForm.sort_order)}
                onChange={(e) =>
                  setProductForm((f) => ({ ...f, sort_order: Number(e.target.value) }))
                }
              />
              <Textarea
                label="الوصف"
                className="span-2"
                value={productForm.description}
                onChange={(v) => setProductForm((f) => ({ ...f, description: v }))}
              />
              <ImagePicker
                value={productForm.image_url}
                onChange={(url) => setProductForm((f) => ({ ...f, image_url: url }))}
                onNotify={(message) => setFlash(message)}
              />
            </div>

            <div className="bill-fields-editor">
              <div className="section-mini-title">
                <strong>حقول يملؤها الزبون</strong>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() =>
                    setProductForm((f) => ({
                      ...f,
                      params: [
                        ...f.params,
                        {
                          name: `field_${f.params.length + 1}`,
                          label: '',
                          type: 'text',
                          required: true,
                        },
                      ],
                    }))
                  }
                >
                  <Icon name="package" size={16} /> إضافة حقل
                </Button>
              </div>
              {productForm.params.map((field, index) => (
                <article key={`${field.name}-${index}`} className="bill-field-row">
                  <Input
                    label="المعرّف"
                    value={field.name}
                    onChange={(e) => updateParam(index, 'name', e.target.value)}
                    placeholder="field_name"
                  />
                  <Input
                    label="الاسم الظاهر"
                    value={field.label}
                    onChange={(e) => updateParam(index, 'label', e.target.value)}
                    placeholder="اسم الحقل الظاهر"
                  />
                  <Select
                    label="النوع"
                    value={field.type}
                    onChange={(v) => updateParam(index, 'type', v)}
                    options={[
                      { value: 'text', label: 'نص' },
                      { value: 'phone', label: 'هاتف' },
                      { value: 'number', label: 'رقم' },
                    ]}
                  />
                  <Checkbox
                    label="مطلوب"
                    checked={field.required}
                    onChange={(checked) => updateParam(index, 'required', checked)}
                  />
                  <button
                    type="button"
                    className="icon-btn danger"
                    onClick={() =>
                      setProductForm((f) => ({
                        ...f,
                        params: f.params.filter((_, i) => i !== index),
                      }))
                    }
                  >
                    <Icon name="close" size={16} />
                  </button>
                </article>
              ))}
            </div>

            <FormActions>
              <Button type="button" disabled={busy} onClick={() => void saveProduct()}>
                <Icon name="edit" size={17} /> حفظ المنتج
              </Button>
              {productForm.id > 0 ? (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setProductForm(EMPTY_PRODUCT)}
                >
                  إلغاء
                </Button>
              ) : null}
            </FormActions>
          </SectionCard>

          <SectionCard title="المنتجات الحالية" icon={<Icon name="bill" size={24} />}>
            {products.length === 0 ? (
              <Empty title="لا توجد منتجات" />
            ) : (
              <ListTable
                headers={['المنتج', 'القسم', 'السعر', 'الحقول', 'الحالة', 'إجراءات']}
              >
                <tbody>
                  {products.map((p) => (
                    <tr key={str(p.id)}>
                      <td>
                        <strong>{str(p.name)}</strong>
                      </td>
                      <td>{str(p.category_name, '-')}</td>
                      <td>
                        <Money value={p.price_syp ?? p.price} />
                      </td>
                      <td>{asParams(p.params).length}</td>
                      <td>{Number(p.is_active) ? 'مفعّل' : 'مخفي'}</td>
                      <td>
                        <div className="table-actions">
                          <button
                            type="button"
                            className="icon-btn"
                            title="تعديل"
                            onClick={() => editProduct(p)}
                          >
                            <Icon name="edit" size={16} />
                          </button>
                          <button
                            type="button"
                            className="icon-btn danger"
                            title="حذف"
                            onClick={() => void removeEntity('product', p.id, str(p.name))}
                          >
                            <Icon name="close" size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </ListTable>
            )}
          </SectionCard>
        </>
      ) : null}

      {tab === 'orders' ? (
        <SectionCard
          title="طلبات الفواتير اليدوية"
          subtitle="اعتمد الطلب بعد تنفيذه، أو ارفضه لإعادة المبلغ إلى المستخدم."
          icon={<Icon name="orders" size={24} />}
        >
          <div className="manual-bill-orders">
            {orders.length === 0 ? (
              <Empty title="لا توجد طلبات" hint="لا توجد طلبات فواتير حالياً." />
            ) : (
              orders.map((order) => {
                const id = str(order.id);
                const open = ['pending', 'processing'].includes(str(order.status));
                const params = orderParams(order);
                return (
                  <article key={id} className="manual-bill-order">
                    <div className="list-head">
                      <div>
                        <strong>
                          #{id} — {str(order.product_name)}
                        </strong>
                        <small>
                          {str(order.user_name)} — {str(order.user_phone)}
                        </small>
                      </div>
                      <StatusBadge status={order.status} />
                    </div>
                    {params.length > 0 ? (
                      <div className="bill-order-data">
                        {params.map(([key, value]) => (
                          <span key={key}>
                            <b>{key}:</b> {value}
                          </span>
                        ))}
                      </div>
                    ) : null}
                    <p>
                      <Money value={order.cost ?? order.price} /> — {formatDateTime(order.created_at)}
                    </p>
                    {open ? (
                      <>
                        <div className="form-grid two-columns">
                          <Input
                            label="رقم مرجعي (اختياري)"
                            value={references[id] || ''}
                            onChange={(e) =>
                              setReferences((prev) => ({ ...prev, [id]: e.target.value }))
                            }
                          />
                          <Input
                            label="ملاحظة للزبون"
                            value={notes[id] || ''}
                            onChange={(e) =>
                              setNotes((prev) => ({ ...prev, [id]: e.target.value }))
                            }
                          />
                        </div>
                        <FormActions>
                          <Button type="button" onClick={() => void resolveOrder(order, 'success')}>
                            <Icon name="check" size={16} /> تم التنفيذ
                          </Button>
                          <Button
                            type="button"
                            variant="danger"
                            onClick={() => void resolveOrder(order, 'reject')}
                          >
                            <Icon name="close" size={16} /> رفض وإرجاع الرصيد
                          </Button>
                        </FormActions>
                      </>
                    ) : null}
                  </article>
                );
              })
            )}
          </div>
        </SectionCard>
      ) : null}
    </ScreenFrame>
  );
}
